import axios from 'axios';
import { toast } from 'react-toastify';
import store from '../redux/store';
import { loadingStopAction } from '../redux/actions/loadingActions';

// Set REACT_APP_API_URL at build time to point at the deployed API.
// Falls back to localhost when running locally so `npm start` keeps working.
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL ||
    (isLocal ? 'http://localhost:5001/api' : 'https://api-server-vip-kalyan.shravanmeena.com/api'),
});

// Endpoints that must not carry an Authorization header. These are matched as
// suffixes because callers pass full paths like "/users/login".
const nonAuthEndpoints = ['/users/login', '/users/register'];

const isNonAuthEndpoint = (url = '') =>
  nonAuthEndpoints.some((endpoint) => url.includes(endpoint));

// Interceptor to add the authentication token to each request
api.interceptors.request.use(
  (config) => {
    if (isNonAuthEndpoint(config.url)) {
      return config;
    }

    // Falls back to localStorage so a page refresh keeps working before the
    // store has been rehydrated.
    const { token } = store.getState().auth;
    const authToken = token || localStorage.getItem('token');

    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    return config;
  },
  // Returning a resolved promise here swallowed request errors entirely.
  (error) => Promise.reject(error)
);

// The API returns "error" from some handlers and "message" from the shared
// error middleware. Reading only "error" showed an empty toast for most
// failures.
const readErrorMessage = (error) => {
  const data = error.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  const message = data?.error || data?.message;

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  if (error.request && !error.response) {
    return 'Cannot reach the server. Please check your connection.';
  }

  return 'Something went wrong. Please try again.';
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Almost every screen dispatches loadingStart and only stops it on the
    // success path, so a failed request used to leave the full screen backdrop
    // up permanently. Clearing it here covers every caller.
    store.dispatch(loadingStopAction());

    const status = error.response?.status;
    const requestUrl = error.config?.url || '';

    // An expired or rejected token should return to the login screen instead of
    // leaving the dashboard in a half broken state. The login call itself is
    // excluded so a wrong password just shows a message.
    if ((status === 401 || status === 403) && !isNonAuthEndpoint(requestUrl)) {
      localStorage.removeItem('token');
      toast.error('Your session has expired. Please log in again.');

      if (!window.location.pathname.includes('/login')) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 1200);
      }

      return Promise.reject(error);
    }

    toast.error(readErrorMessage(error));

    return Promise.reject(error);
  }
);

export default api;
